import { listConversations, deleteConversation, getResults } from '../api.js';
import { getCurrentUser } from '../auth.js';
import { getJobsCopy, translate } from '../i18n.js';
import { registerViewCleanup } from '../utils/viewLifecycle.js';

const html = String.raw;
const JOBS_SCROLL_KEY = 'anforderungsai.jobs.scrollY';
const BACK_KEY = 'anforderungsai:conversation:back';

const STATUS_ORDER = ['UPLOADED', 'TRANSCRIBING', 'EXTRACTING', 'READY'];
const STEP_INDEX = STATUS_ORDER.reduce((acc, step, index) => ({ ...acc, [step]: index }), {});

const normalizeValue = (value) => String(value ?? '').trim();

const collectTextParts = (value, parts) => {
  if (value === null || value === undefined) return;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    parts.push(String(value));
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectTextParts(item, parts));
    return;
  }
  if (typeof value === 'object') {
    Object.values(value).forEach((item) => collectTextParts(item, parts));
  }
};

const buildArtifactsSearchText = (payload) => {
  const parts = [];
  const requirements = payload?.requirements ?? payload?.artifacts?.requirements ?? [];
  const userStories = payload?.userStories ?? payload?.artifacts?.userStories ?? [];
  requirements.forEach((item) => collectTextParts(item, parts));
  userStories.forEach((item) => collectTextParts(item, parts));
  return parts.join(' ');
};

const formatMeetingDate = (value) => {
  const normalized = normalizeValue(value);
  if (!normalized) return '—';
  const parsed = Date.parse(`${normalized}T00:00:00`);
  if (!Number.isFinite(parsed)) return normalized;
  return new Date(parsed).toLocaleDateString();
};

const getMeetingTimestamp = (value) => {
  const normalized = normalizeValue(value);
  if (!normalized) return 0;
  const parsed = Date.parse(`${normalized}T00:00:00`);
  if (!Number.isFinite(parsed)) return 0;
  return parsed;
};

const getEngineInfo = (conversation, jobsCopy) => {
  const engine = conversation?.transcriptionEngine ?? conversation?.engine;
  if (!engine) {
    return { label: jobsCopy.engineLabels?.none ?? '—', variant: 'none' };
  }
  const normalized = String(engine).toLowerCase();
  if (normalized === 'aws-transcribe') return { label: 'AWS Transcribe', variant: 'aws' };
  if (normalized === 'mistral' || normalized === 'voxtral') return { label: 'Mistral (Voxtral)', variant: 'mistral' };
  return { label: engine, variant: 'other' };
};

const isAppFactoryConversation = (conversation) => {
  const sessionType = conversation.sessionType ? String(conversation.sessionType).toLowerCase() : '';
  const mode = conversation.mode ? String(conversation.mode).toLowerCase() : '';
  const status = conversation.status ? String(conversation.status).toUpperCase() : '';
  return sessionType === 'appfactory'
    || mode === 'appfactory'
    || status === 'APP_FACTORY_ACTIVE'
    || Boolean(conversation.appFactorySessionId);
};

const getSourceInfo = (conversation, jobsCopy) => {
  if (isAppFactoryConversation(conversation)) {
    const sessionLabel = jobsCopy.sourceLabels.appFactory ?? 'App Factory';
    return { label: sessionLabel, variant: 'appfactory' };
  }

  let normalizedMode = conversation.mode ? String(conversation.mode).toLowerCase() : '';
  const rawStatus = conversation.status ? String(conversation.status).toUpperCase() : '';
  if (!normalizedMode) {
    if (conversation.originalFilename) {
      normalizedMode = 'upload';
    } else if (rawStatus === 'LIVE_ACTIVE') {
      normalizedMode = 'live';
    }
  }

  let baseLabel;
  let variant;
  if (normalizedMode === 'live') {
    baseLabel = jobsCopy.sourceLabels.live;
    variant = 'live';
  } else if (normalizedMode === 'transcript') {
    baseLabel = jobsCopy.sourceLabels.transcript ?? jobsCopy.sourceLabels.upload;
    variant = 'transcript';
  } else if (normalizedMode === 'upload' || normalizedMode === 'batch' || normalizedMode === 'standard' || !normalizedMode) {
    baseLabel = jobsCopy.sourceLabels.upload;
    variant = 'upload';
  } else {
    baseLabel = jobsCopy.sourceLabels.unknown;
    variant = 'unknown';
  }

  return { label: baseLabel, variant };
};

const renderPill = (label, variant, baseClass) => {
  const safeLabel = label || '—';
  const variantClass = variant ? `${baseClass}--${variant}` : '';
  return html`<span class="pill ${baseClass} ${variantClass}">${safeLabel}</span>`;
};

const getStatusInfo = (conversation, jobsCopy) => {
  const rawStatus = conversation.status ? String(conversation.status).toUpperCase() : '';
  if (rawStatus === 'ERROR' || rawStatus === 'FAILED') {
    return { label: jobsCopy.failedLabel, variant: 'error' };
  }
  const statusLabels = jobsCopy.statusLabels ?? {};
  if (rawStatus === 'APP_FACTORY_ACTIVE' || rawStatus === 'LIVE_ACTIVE' || rawStatus === 'LIVE') {
    const label = statusLabels[rawStatus] ?? 'Aktiv';
    return { label, variant: 'running' };
  }
  if (STATUS_ORDER.includes(rawStatus)) {
    const label = jobsCopy.steps[rawStatus] ?? rawStatus;
    const variant = rawStatus === 'READY' ? 'success' : 'running';
    return { label, variant };
  }
  const fallback = jobsCopy.statusUnknown ?? '—';
  const label = (statusLabels[rawStatus] ?? rawStatus) || fallback;
  return { label, variant: 'pending' };
};

const getStepState = (status, stepId) => {
  if (status === 'ERROR') {
    if (stepId === 'READY') return 'error';
    return 'done';
  }
  const activeIndex = STEP_INDEX[status] ?? -1;
  const stepIndex = STEP_INDEX[stepId] ?? -1;
  if (stepIndex === -1) return 'pending';
  if (activeIndex === -1) return 'pending';
  if (stepIndex < activeIndex) return 'done';
  if (stepIndex === activeIndex) {
    return status === 'READY' ? 'done' : 'current';
  }
  return 'pending';
};

const renderStatusSteps = (conversation, jobsCopy) => {
  const status = conversation.status ?? 'UNBEKANNT';
  const steps = STATUS_ORDER.map((stepId) => {
    const state = getStepState(status, stepId);
    return html`
      <li class="${state}">
        <span class="dot"></span>
        <span class="label">${jobsCopy.steps[stepId]}</span>
      </li>
    `;
  });
  if (status === 'ERROR') {
    steps.push(html`
      <li class="error">
        <span class="dot"></span>
        <span class="label">${jobsCopy.failedLabel}</span>
      </li>
    `);
  }
  return html`
    <ol class="status-steps">${steps.join('')}</ol>
    ${conversation.error ? `<p class="muted small">${conversation.error}</p>` : ''}
  `;
};

const renderRow = (conversation, jobsCopy, { isAdmin }) => {
  const createdDate = conversation.createdAt
    ? new Date(conversation.createdAt)
    : conversation.updatedAt
      ? new Date(conversation.updatedAt)
      : null;
  const createdAtFormatted = createdDate
    ? html`<span>${createdDate.toLocaleDateString()}</span><br /><span class="muted small">${createdDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>`
    : html`<span>-</span>`;
  const meetingLabel = formatMeetingDate(conversation.meetingDate);
  const customer = conversation.customerId || '–';
  const project = conversation.projectId || '–';
  const sourceInfo = getSourceInfo(conversation, jobsCopy);
  const engineInfo = getEngineInfo(conversation, jobsCopy);
  const statusInfo = getStatusInfo(conversation, jobsCopy);
  const detailHref = `#/conversation/${conversation.conversationId}`;
  return html`
    <tr data-conversation-id="${conversation.conversationId}">
      <td>
        <a href="${detailHref}" class="jobs-detail-link"><strong>${customer}</strong></a>
      </td>
      <td><a href="${detailHref}" class="jobs-detail-link">${project}</a></td>
      <td class="meeting-cell">${meetingLabel}</td>
      <td class="status-cell">
        <div class="status-pill-wrapper">
          <span class="status-pill status-pill--${statusInfo.variant}">${statusInfo.label}</span>
          <div class="status-tooltip">
            ${renderStatusSteps(conversation, jobsCopy)}
          </div>
        </div>
      </td>
      <td class="source-cell">${renderPill(sourceInfo.label, sourceInfo.variant, 'source-pill')}</td>
      <td class="engine-cell">${renderPill(engineInfo.label, engineInfo.variant, 'engine-pill')}</td>
      <td class="updated-cell">${createdAtFormatted}</td>
      ${isAdmin
        ? html`
            <td>
              <button
                type="button"
                class="delete-icon-button admin-only-control"
                title="${jobsCopy.deleteLabel}"
                aria-label="${jobsCopy.deleteLabel}"
                data-delete-job="${conversation.conversationId}"
              >
                X
              </button>
            </td>
          `
        : ''}
    </tr>
  `;
};

export const renderJobsView = (container) => {
  const user = getCurrentUser();
  const isAdmin = user?.role === 'admin';
  const copy = getJobsCopy();
  let searchQuery = '';
  let allConversations = [];
  const artifactsSearchCache = new Map();
  let searchIndexPromise = null;
  let sortColumn = 'created';
  let sortDirection = 'desc';
  let restoredScroll = false;
  container.innerHTML = html`
    <div class="stack jobs-view">
      <div class="view-header row" style="justify-content: space-between; align-items: flex-start; gap: 1rem;">
        <div class="stack tight">
          <h1>${copy.title}</h1>
          ${copy.description ? `<p class="muted">${copy.description}</p>` : ''}
        </div>
        <button type="button" class="ghost-button" id="refresh">${copy.refresh}</button>
      </div>
      <section class="card stack">
        <div class="jobs-search">
          <input
            type="search"
            id="jobs-search"
            placeholder="${copy.searchPlaceholder ?? ''}"
            aria-label="${copy.searchLabel ?? copy.searchPlaceholder ?? 'Suche'}"
          />
          <div class="jobs-search-status muted small" id="jobs-search-status" aria-live="polite"></div>
        </div>
        <div id="jobs-content">${translate('common.loading')}</div>
      </section>
    </div>
  `;

  const content = container.querySelector('#jobs-content');
  const refreshButton = container.querySelector('#refresh');
  const searchInput = container.querySelector('#jobs-search');
  const searchStatus = container.querySelector('#jobs-search-status');

  const restoreScroll = () => {
    if (restoredScroll) return;
    const saved = sessionStorage.getItem(JOBS_SCROLL_KEY);
    if (!saved) return;
    const value = Number(saved);
    sessionStorage.removeItem(JOBS_SCROLL_KEY);
    if (Number.isFinite(value)) {
      window.scrollTo({ top: value, behavior: 'auto' });
    }
    restoredScroll = true;
  };

  if (content) {
    content.addEventListener('click', (event) => {
      const sortButton = event.target.closest('[data-sort-column]');
      if (sortButton) {
        event.preventDefault();
        toggleSortColumn(sortButton.dataset.sortColumn);
        return;
      }
    });
    content.addEventListener('click', (event) => {
      const link = event.target.closest('a[href^="#/conversation/"]');
      if (!link) return;
      sessionStorage.setItem(JOBS_SCROLL_KEY, String(window.scrollY));
      sessionStorage.setItem(BACK_KEY, 'jobs');
    });
    content.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-delete-job]');
      if (!button) return;
      const conversationId = button.dataset.deleteJob;
      if (!conversationId) return;
      const confirmText = copy.deleteConfirm ?? 'Diesen Job wirklich löschen?';
      if (!window.confirm(confirmText)) return;
      button.disabled = true;
      try {
        await deleteConversation(conversationId);
        await load();
      } catch (error) {
        console.error('Failed to delete conversation', error);
        alert(error?.message || copy.deleteError || 'Löschen fehlgeschlagen.');
        button.disabled = false;
      }
    });
  }

  const buildSearchText = (conversation) => {
    const sourceInfo = getSourceInfo(conversation, copy);
    const engineInfo = getEngineInfo(conversation, copy);
    const statusInfo = getStatusInfo(conversation, copy);
    const artifactsText = artifactsSearchCache.get(conversation.conversationId);
    return [
      conversation.customerId,
      conversation.projectId,
      conversation.status,
      statusInfo.label,
      sourceInfo.label,
      engineInfo.label,
      conversation.error,
      conversation.domain,
      conversation.originalFilename,
      conversation.meetingDate,
      conversation.conversationId,
      artifactsText,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  };

  const updateSearchStatus = (loading) => {
    if (!searchStatus) return;
    if (!searchQuery.trim()) {
      searchStatus.textContent = '';
      searchStatus.style.display = 'none';
      return;
    }
    if (loading) {
      searchStatus.textContent = copy.searchLoading ?? 'Suche lädt…';
      searchStatus.style.display = '';
      return;
    }
    searchStatus.textContent = '';
    searchStatus.style.display = 'none';
  };

  const loadArtifactsForSearch = async () => {
    const missing = allConversations.filter(
      (conversation) => conversation.conversationId && !artifactsSearchCache.has(conversation.conversationId),
    );
    if (!missing.length) return;
    const concurrency = Math.min(4, missing.length);
    let index = 0;
    await Promise.all(
      Array.from({ length: concurrency }, () => (async () => {
        while (index < missing.length) {
          const current = missing[index];
          index += 1;
          if (!current?.conversationId) continue;
          try {
            const payload = await getResults(current.conversationId);
            const text = buildArtifactsSearchText(payload);
            artifactsSearchCache.set(current.conversationId, text.toLowerCase());
          } catch (error) {
            console.warn('Failed to load search artifacts', current.conversationId, error);
            artifactsSearchCache.set(current.conversationId, '');
          }
        }
      })()),
    );
  };

  const ensureSearchIndex = () => {
    if (!searchQuery.trim()) {
      updateSearchStatus(false);
      return;
    }
    const missing = allConversations.some(
      (conversation) => conversation.conversationId && !artifactsSearchCache.has(conversation.conversationId),
    );
    if (!missing) {
      updateSearchStatus(false);
      return;
    }
    if (searchIndexPromise) {
      updateSearchStatus(true);
      return;
    }
    updateSearchStatus(true);
    searchIndexPromise = loadArtifactsForSearch()
      .catch((error) => {
        console.warn('Failed to build job search index', error);
      })
      .finally(() => {
        searchIndexPromise = null;
        updateSearchStatus(false);
        renderTable();
      });
  };

  const getSortValue = (conversation, column) => {
    const customer = normalizeValue(conversation.customerId || conversation.customer);
    const project = normalizeValue(conversation.projectId || conversation.project);
    const statusInfo = getStatusInfo(conversation, copy);
    const sourceInfo = getSourceInfo(conversation, copy);
    const engineInfo = getEngineInfo(conversation, copy);
    switch (column) {
      case 'customer':
        return customer;
      case 'project':
        return project;
      case 'status':
        return statusInfo.label.toLowerCase();
      case 'meeting':
        return getMeetingTimestamp(conversation.meetingDate);
      case 'source':
        return sourceInfo.label.toLowerCase();
      case 'engine':
        return engineInfo.label.toLowerCase();
      case 'created':
      default:
        return conversation.createdAt ? Date.parse(conversation.createdAt) : conversation.updatedAt ? Date.parse(conversation.updatedAt) : 0;
    }
  };

  const sortConversations = (conversations) => {
    const result = [...conversations];
    result.sort((a, b) => {
      const aValue = getSortValue(a, sortColumn);
      const bValue = getSortValue(b, sortColumn);
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  };

  const getSortedConversations = () => sortConversations(allConversations);

  const getFilteredConversations = () => {
    const base = getSortedConversations();
    const query = searchQuery.trim().toLowerCase();
    if (!query) return base;
    return base.filter((conversation) => buildSearchText(conversation).includes(query));
  };

  const toggleSortColumn = (column) => {
    if (sortColumn === column) {
      sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      sortColumn = column;
      sortDirection = 'asc';
    }
    renderTable();
  };

  const renderSortHeader = (column, label) => html`
    <button
      type="button"
      class="jobs-sort-button ${sortColumn === column ? 'active' : ''}"
      data-sort-column="${column}"
    >
      <span>${label}</span>
      <span class="jobs-sort-icon" aria-hidden="true">
        ${sortColumn === column ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </button>
  `;

  const renderTable = () => {
    ensureSearchIndex();
    const filtered = getFilteredConversations();
    if (!filtered.length) {
      const message = allConversations.length ? (searchQuery ? copy.noMatches : copy.empty) : copy.empty;
      content.innerHTML = `<p class="muted">${message}</p>`;
      return;
    }
    const rows = filtered
      .map((conversation) => renderRow(conversation, copy, { isAdmin }))
      .join('');
    content.innerHTML = html`
      <table class="jobs-table">
        <thead>
          <tr>
            <th>${renderSortHeader('customer', copy.columns.customer)}</th>
            <th>${renderSortHeader('project', copy.columns.project)}</th>
            <th>${renderSortHeader('meeting', copy.columns.meeting ?? 'Meeting')}</th>
            <th>${renderSortHeader('status', copy.columns.status)}</th>
            <th>${renderSortHeader('source', copy.columns.source)}</th>
            <th>${renderSortHeader('engine', copy.columns.engine)}</th>
            <th>${renderSortHeader('created', copy.columns.updated)}</th>
            ${isAdmin ? `<th class="admin-only-surface">${copy.columns.delete}</th>` : ''}
          </tr>
        </thead>
        <tbody>
          ${rows}
        </tbody>
      </table>
    `;
  };

  const updateTable = () => {
    const tbody = container.querySelector('.jobs-table tbody');
    if (!tbody) {
      renderTable();
      return;
    }
    const filtered = getFilteredConversations();
    if (!filtered.length) {
      renderTable();
      return;
    }
    tbody.innerHTML = filtered
      .map((conversation) => renderRow(conversation, copy, { isAdmin }))
      .join('');
  };

  const load = async () => {
    const tableExists = Boolean(content.querySelector('.jobs-table'));
    const scrollTop = content.scrollTop;
    try {
      allConversations = await listConversations({ limit: 200 });
      if (!allConversations.length) {
        content.innerHTML = `<p class="muted">${copy.empty}</p>`;
        return;
      }
      if (!tableExists) {
        renderTable();
      } else {
        updateTable();
        content.scrollTop = scrollTop;
      }
      restoreScroll();
      ensureSearchIndex();
    } catch (error) {
      console.error('Failed to load jobs', error);
      content.innerHTML = `<p class="muted">${copy.loadError}</p>`;
    }
  };

  refreshButton.addEventListener('click', load);
  if (searchInput) {
    searchInput.addEventListener('input', (event) => {
      searchQuery = event.target.value || '';
      ensureSearchIndex();
      renderTable();
    });
  }
  let pollId = window.setInterval(load, 8000);
  registerViewCleanup(() => {
    refreshButton.removeEventListener('click', load);
    if (searchInput) {
      searchInput.replaceWith(searchInput.cloneNode(true));
    }
    if (pollId) {
      clearInterval(pollId);
      pollId = null;
    }
  });
  load();
};
